import type {
  ConversationThread,
  DecryptedNotification,
  DigestModel,
  StorageAdapter,
} from "@reflecto/client-core";
import type { AIAvailability, AIProvider } from "@reflecto/client-core/ai";
import { useCallback, useState } from "react";
import { isDigestEmpty } from "./digestModel";
import { useTriageDriver } from "./useTriageDriver";

export type GlanceView =
  | { kind: "idle" }
  | { kind: "needs-download" }
  | { kind: "downloading"; loaded: number; total?: number }
  | { kind: "caught-up" }
  | { kind: "error"; reason: "ai"; canRetry: boolean }
  | { kind: "digest"; model: DigestModel; leadStreaming: boolean };

export interface UseGlanceOptions {
  provider: AIProvider;
  adapter: StorageAdapter;
  availability: AIAvailability;
  loadNotifications: () => Promise<DecryptedNotification[]>;
  loadConversations: () => Promise<ConversationThread[]>;
  prefs: { hiddenApps: string[]; dndBypassApps?: string[]; publicMode?: boolean };
  downloadProgress?: { loaded: number; total?: number };
  outputLanguage?: string;
  /**
   * Host-derived signal that changes when the live notifications/conversations change (incl. async
   * hydration on cold load). Forwarded to the ambient triage driver so the digest rebuilds when the
   * host's data finally arrives — not only on storage-write change events. See `useTriageDriver`.
   */
  dataVersion?: unknown;
  /** Scan window; defaults to 24h. */
  windowMs?: number;
}

export interface UseGlanceResult {
  view: GlanceView;
  /** Surface the card (header ✨). Idempotent. */
  surface: () => void;
  /** Close → idle. */
  close: () => void;
  /** Flag a component-owned AI error (e.g. a rejected startDownload). Recoverable via the Retry CTA → surface(). */
  fail: () => void;
}

const DEFAULT_WINDOW_MS = 24 * 60 * 60_000;

export function useGlance(options: UseGlanceOptions): UseGlanceResult {
  const {
    provider,
    adapter,
    availability,
    loadNotifications,
    loadConversations,
    prefs,
    downloadProgress,
    outputLanguage,
  } = options;
  const enabled = availability === "available";

  // Ambient digest — runs whenever the host mounts <Glance>, independent of surfacing, so it's warm.
  const { digest, leadStreaming } = useTriageDriver({
    provider,
    adapter,
    enabled,
    loadNotifications,
    loadConversations,
    // Only hiddenApps drives gather/triage. publicMode is not forwarded (reserved + in-memory-only in V1,
    // never on — client-core/CLAUDE.md); the old engine redacted oneLiners to "New activity" under it as a
    // screen-share guard — re-add that redaction here when publicMode is actually wired. dndBypassApps gates
    // ring-through, not digest inclusion, so it's dropped too.
    prefs: { hiddenApps: prefs.hiddenApps },
    windowMs: options.windowMs ?? DEFAULT_WINDOW_MS,
    outputLanguage,
    dataVersion: options.dataVersion,
  });

  const [surfaced, setSurfaced] = useState(false);
  const [failed, setFailed] = useState(false);

  const surface = useCallback(() => {
    setFailed(false);
    setSurfaced(true);
  }, []);
  // Reset failed too (symmetric with surface()): a future surfacing path that bypasses surface()
  // mustn't inherit a stale failed=true and render a spurious error card.
  const close = useCallback(() => {
    setSurfaced(false);
    setFailed(false);
  }, []);
  const fail = useCallback(() => setFailed(true), []);

  let view: GlanceView;
  if (!surfaced) {
    view = { kind: "idle" };
  } else if (failed) {
    // A component fail() (e.g. a rejected startDownload) → retryable error. surface() re-runs the
    // pass, so Retry is always offered here.
    view = { kind: "error", reason: "ai", canRetry: true };
  } else if (availability === "downloadable") {
    view = { kind: "needs-download" };
  } else if (availability === "downloading") {
    view = {
      kind: "downloading",
      loaded: downloadProgress?.loaded ?? 0,
      total: downloadProgress?.total,
    };
  } else if (!enabled) {
    // Mid-session capability regression with the card already open: availability dropped to
    // unsupported / unavailable / requirements-unmet (e.g. a refresh() after a failed download).
    // Not recoverable via surface(), so no Retry — the X is the way out. (At initial surfacing the
    // button gate guarantees a capable state, but an open card can outlive a regression.)
    view = { kind: "error", reason: "ai", canRetry: false };
  } else if (!digest || isDigestEmpty(digest)) {
    // Available model, empty 24h window → the warm caught-up state.
    view = { kind: "caught-up" };
  } else {
    view = { kind: "digest", model: digest, leadStreaming };
  }

  return { view, surface, close, fail };
}
